import React, { useState, useRef, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid, TextField, Select, MenuItem,
  FormControl, InputLabel, Button, Chip, Checkbox, FormControlLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  CircularProgress, Alert, Divider, Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import MapIcon from '@mui/icons-material/Map';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { saveReTrackSectors } from '../../utils/reTracksService';
import TrackMapLeaflet from './TrackMapLeaflet';

const TIPI = [
  { value: '',           label: '— non specificato —' },
  { value: 'hairpin',    label: 'Tornante / Hairpin' },
  { value: 'chicane',    label: 'Chicane' },
  { value: 'sweeper',    label: 'Curva veloce (Sweeper)' },
  { value: 'complex',    label: 'Complesso di curve' },
  { value: 'rettilineo', label: 'Rettilineo' },
  { value: 'altro',      label: 'Altro' },
];

const VELOCITA = [
  { value: '',       label: '—' },
  { value: 'lenta',  label: 'Lenta  (<80 km/h)' },
  { value: 'media',  label: 'Media  (80–140 km/h)' },
  { value: 'veloce', label: 'Veloce (>140 km/h)' },
];

function SectorEditDialog({ sector, index, onSave, onClose }) {
  const [form, setForm] = useState({
    nome:               sector.nome               || `Settore ${sector.sector}`,
    tipo:               sector.tipo               || '',
    banking_deg:        sector.banking_deg        ?? 0,
    velocita:           sector.velocita           || '',
    precede_rettilineo: sector.precede_rettilineo || false,
    note:               sector.note               || '',
  });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Modifica Settore {sector.sector}
        <Chip label={`S${sector.sector}`} size="small" color="secondary" sx={{ ml: 1, fontSize: 11 }} />
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <TextField
              label="Nome settore"
              fullWidth size="small"
              value={form.nome}
              onChange={e => set('nome', e.target.value)}
              placeholder={`Settore ${sector.sector}`}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo</InputLabel>
              <Select value={form.tipo} label="Tipo" onChange={e => set('tipo', e.target.value)}>
                {TIPI.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Velocità</InputLabel>
              <Select value={form.velocita} label="Velocità" onChange={e => set('velocita', e.target.value)}>
                {VELOCITA.map(v => <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Banking (gradi)"
              type="number"
              fullWidth size="small"
              value={form.banking_deg}
              onChange={e => set('banking_deg', parseFloat(e.target.value) || 0)}
              helperText="Negativo = contropendenza · Positivo = sopraelevazione"
              inputProps={{ min: -15, max: 15, step: 0.5 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', pt: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.precede_rettilineo}
                    onChange={e => set('precede_rettilineo', e.target.checked)}
                    color="secondary"
                  />
                }
                label="Precede un rettilineo lungo"
              />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Note tecniche"
              fullWidth multiline rows={3} size="small"
              value={form.note}
              onChange={e => set('note', e.target.value)}
              placeholder="Es: Cieco in ingresso, apex ritardato, fondo sconnesso in uscita…"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Annulla</Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={() => onSave(index, form)}
        >
          Salva
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Editor interattivo della pista.
 *
 * Props:
 *   trackSlug  – slug della pista (es. "castelletto_branduzzo")
 *   reTrack    – dati da Firestore re_tracks (null se non ancora popolata)
 */
export default function TrackMapEditor({ trackSlug, reTrack }) {
  const [sectors, setSectors]           = useState(reTrack?.sectors || []);
  const [editingIdx, setEditingIdx]     = useState(null);
  const [saving, setSaving]             = useState(false);
  const [saved, setSaved]               = useState(false);
  const [activeSector, setActiveSector] = useState(null);

  const handleSaveSector = useCallback((idx, metadata) => {
    setSectors(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], ...metadata };
      return updated;
    });
    setEditingIdx(null);
  }, []);

  const handleSaveAll = useCallback(async () => {
    if (!trackSlug) return;
    setSaving(true);
    try {
      await saveReTrackSectors(trackSlug, sectors);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      console.error('Errore salvataggio pista:', e);
    } finally {
      setSaving(false);
    }
  }, [trackSlug, sectors]);

  // Adattatore dati per TrackMapLeaflet
  const leafletTrackData = reTrack ? {
    track_gps:       (reTrack.gps_trace || []).filter(p => p.lat && p.lon),
    sectors:         sectors.map(s => ({ ...s, delta_vs_best_s: 0 })),
    reference_gps:   [],
    session:         reTrack.name || '',
    best_lap_time:   '',
    best_theoretical: '',
  } : null;

  const handleSectorClickEditor = (sectorNum) => {
    setActiveSector(sectorNum);
    const idx = sectors.findIndex(s => s.sector === sectorNum);
    if (idx >= 0) setEditingIdx(idx);
  };

  if (!reTrack) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <MapIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Nessuna mappa disponibile
        </Typography>
        <Typography variant="body2" color="text.disabled">
          Analizza una sessione su questa pista con il Race Engineer per generare automaticamente la mappa e i settori.
        </Typography>
      </Box>
    );
  }

  const completedCount = sectors.filter(s => s.tipo && s.nome && s.nome !== `Settore ${s.sector}`).length;

  return (
    <Box>
      {/* Header con progress */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            Configurazione Settori
          </Typography>
          <Chip
            label={`${completedCount}/${sectors.length} configurati`}
            size="small"
            color={completedCount === sectors.length ? 'success' : 'default'}
            variant="outlined"
          />
          {reTrack.contributions > 0 && (
            <Chip
              label={`${reTrack.contributions} sessioni caricate`}
              size="small"
              variant="outlined"
              sx={{ fontSize: 10 }}
            />
          )}
        </Box>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : saved ? <CheckCircleIcon /> : <SaveIcon />}
          onClick={handleSaveAll}
          disabled={saving}
          color={saved ? 'success' : 'primary'}
        >
          {saving ? 'Salvataggio…' : saved ? 'Salvato!' : 'Salva tutto'}
        </Button>
      </Box>

      <Grid container spacing={2.5}>
        {/* Mappa Leaflet satellite */}
        <Grid item xs={12} md={6}>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600}
              sx={{ display: 'block', mb: 0.5, pl: 0.5 }}>
              MAPPA CIRCUITO — clicca un settore per modificarlo
            </Typography>
            {leafletTrackData?.track_gps?.length >= 10 ? (
              <TrackMapLeaflet
                compact
                trackData={leafletTrackData}
                activeSector={activeSector}
                onSectorClick={handleSectorClickEditor}
              />
            ) : (
              <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', p: 3, textAlign: 'center' }}>
                <MapIcon sx={{ color: 'text.disabled', fontSize: 36, mb: 1 }} />
                <Typography variant="caption" color="text.disabled" display="block">
                  Analizza una sessione su questa pista con il Race Engineer<br />
                  per generare automaticamente la mappa GPS satellite.
                </Typography>
              </Paper>
            )}
          </Box>
        </Grid>

        {/* Lista settori editabile */}
        <Grid item xs={12} md={6}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {sectors.map((s, i) => {
              const isConfigured = s.tipo && s.nome && s.nome !== `Settore ${s.sector}`;
              return (
                <Box
                  key={s.sector}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    p: 1, border: '1px solid', borderColor: 'divider',
                    borderRadius: 1.5, cursor: 'pointer',
                    '&:hover': { bgcolor: 'rgba(63,81,181,0.05)', borderColor: 'secondary.main' },
                  }}
                  onClick={() => setEditingIdx(i)}
                >
                  <Box sx={{
                    width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                    bgcolor: isConfigured ? 'rgba(76,175,80,0.2)' : 'rgba(255,255,255,0.06)',
                    border: '1px solid', borderColor: isConfigured ? 'success.main' : 'divider',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: isConfigured ? 'success.main' : 'text.secondary' }}>
                      {s.sector}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {s.nome || `Settore ${s.sector}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[s.tipo, s.velocita, s.banking_deg ? `${s.banking_deg}°` : null]
                        .filter(Boolean).join(' · ') || 'Non configurato'}
                    </Typography>
                  </Box>
                  <IconButton size="small" sx={{ flexShrink: 0 }}>
                    <EditIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              );
            })}
          </Box>
        </Grid>
      </Grid>

      {/* Dialog modifica settore */}
      {editingIdx !== null && (
        <SectorEditDialog
          sector={sectors[editingIdx]}
          index={editingIdx}
          onSave={handleSaveSector}
          onClose={() => setEditingIdx(null)}
        />
      )}
    </Box>
  );
}
